import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Users, Hash, Server, Calendar, Video, MessageSquare, ShieldCheck, TrendingUp, Package, Trophy, Hammer, Globe } from "lucide-react";
import { globalSearch, fetchSearchTrending, logSearchQuery } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { sfx } from "@/lib/sounds";

const TYPES = [
  { key: "all",          label: "All",          icon: Search },
  { key: "creators",     label: "Creators",     icon: Video },
  { key: "communities",  label: "Communities",  icon: Hash },
  { key: "servers",      label: "Servers",      icon: Server },
  { key: "events",       label: "Events",       icon: Calendar },
  { key: "videos",       label: "Videos",       icon: Video },
  { key: "products",     label: "Products",     icon: Package },
  { key: "builds",       label: "Builds",       icon: Hammer },
  { key: "leaderboards", label: "Leaderboards", icon: Trophy },
  { key: "pages",        label: "Pages",        icon: Globe },
];

const RESULT_META = {
  creator:     { icon: Video,         color: "text-pink-300",   label: "Creator" },
  community:   { icon: Hash,          color: "text-purple-300", label: "Community" },
  server:      { icon: Server,        color: "text-green-300",  label: "Server" },
  event:       { icon: Calendar,      color: "text-orange-300", label: "Event" },
  video:       { icon: Video,         color: "text-red-300",    label: "Video" },
  application: { icon: ShieldCheck,   color: "text-yellow-300", label: "Application" },
  product:     { icon: Package, color: "text-emerald-300", label: "Product" },
  build:       { icon: Hammer,  color: "text-amber-300",   label: "Build" },
  leaderboard: { icon: Trophy,  color: "text-yellow-300",  label: "Leaderboard" },
  page:        { icon: Globe,   color: "text-sky-300",     label: "Page" },
};

function HighlightText({ text, ranges }) {
  if (!text || !ranges || ranges.length === 0) return <>{text}</>;
  const parts = [];
  let cursor = 0;
  for (const [s, e] of ranges) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark key={s} className="search-highlight">
        {text.slice(s, e)}
      </mark>
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function ResultCard({ r, idx }) {
  const M = RESULT_META[r.type] || RESULT_META.user;
  const Icon = M.icon;
  const hl = r.highlight || {};
  const isExternal = r.url?.startsWith("http");
  const props = isExternal
    ? { as: "a", href: r.url, target: "_blank", rel: "noopener noreferrer" }
    : { to: r.url || "/" };
  const Tag = isExternal ? "a" : Link;
  return (
    <Tag
      {...(isExternal ? { href: r.url, target: "_blank", rel: "noopener noreferrer" } : { to: r.url || "/" })}
      data-testid={`search-page-result-${idx}`}
      onClick={() => sfx.click()}
      className="group block glass border border-white/10 hover:border-[#FFBF00]/40 rounded-xl p-4 transition-colors"
    >
      <div className="flex gap-4">
        {r.avatar ? (
          <img
            src={r.avatar}
            alt=""
            className="w-16 h-16 rounded-lg object-cover bg-white/5 shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className={`w-16 h-16 rounded-lg flex items-center justify-center bg-white/5 shrink-0 ${M.color}`}>
            <Icon size={22} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${M.color} bg-white/5`}>
              {M.label}
            </span>
            {r.meta?.featured && (
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded text-black bg-[#FFBF00]">
                Featured
              </span>
            )}
            {r.meta?.verified && (
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded text-blue-300 bg-blue-500/10 border border-blue-500/30">
                Verified
              </span>
            )}
          </div>
          <div className="text-white font-semibold truncate group-hover:text-[#FFBF00] transition-colors">
            <HighlightText text={r.title} ranges={hl.title} />
          </div>
          {r.subtitle && <div className="text-sm text-white/60 mt-1 line-clamp-2"><HighlightText text={r.subtitle} ranges={hl.subtitle} /></div>}
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-white/40">
            {r.meta?.members !== undefined && <span>{r.meta.members} members</span>}
            {r.meta?.players_online !== undefined && <span>{r.meta.players_online} online</span>}
            {r.meta?.views !== undefined && <span>{r.meta.views.toLocaleString()} views</span>}
            {r.meta?.status && <span className="uppercase tracking-widest">{r.meta.status}</span>}
            {r.meta?.category && <span>#{r.meta.category}</span>}
            {r.meta?.kind && r.type === "video" && <span className="uppercase tracking-widest">{r.meta.kind}</span>}
          </div>
        </div>
      </div>
    </Tag>
  );
}

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const q = params.get("q") || "";
  const type = params.get("type") || "all";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);

  const typeTabs = useMemo(() => [...TYPES], []);

  useEffect(() => {
    document.title = q ? `Search: ${q} — LostDC` : "Search — LostDC";
    return () => { document.title = "LostDC Network"; };
  }, [q]);

  useEffect(() => {
    fetchSearchTrending(12).then(setTrending).catch(() => setTrending([]));
  }, []);

  useEffect(() => {
    if (!q.trim() && type === "all") { setData(null); return; }
    setLoading(true);
    globalSearch(q, type, 20)
      .then((res) => { setData(res); if (q.trim()) logSearchQuery(q.trim()); })
      .catch(() => setData({ total: 0, top: [], results: {} }))
      .finally(() => setLoading(false));
  }, [q, type]);

  const setType = (t) => setParams({ q, ...(t !== "all" ? { type: t } : {}) });

  const currentResults = useMemo(() => {
    if (!data) return [];
    return type === "all" ? (data.top || []) : ((data.results && data.results[type]) || []);
  }, [data, type]);

  const perTypeCounts = useMemo(() => {
    if (!data?.results) return {};
    const out = {};
    for (const k of Object.keys(data.results)) out[k] = data.results[k].length;
    return out;
  }, [data]);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 text-[10px] text-[#FFBF00] uppercase tracking-[0.3em] mb-3">
            <Search size={12} /> Global Search
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2">
            {q ? (
              <>Results for <span className="text-[#FFBF00]">&quot;{q}&quot;</span></>
            ) : (
              "Explore the network"
            )}
          </h1>
          {data && (
            <div className="text-white/50 text-base" data-testid="search-page-total">
              {data.total} result{data.total === 1 ? "" : "s"}
              {type !== "all" && <> in <span className="text-white/80">{typeTabs.find(t => t.key === type)?.label}</span></>}
            </div>
          )}
        </motion.div>

        {/* Type Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="search-page-tabs">
          {typeTabs.map((t) => {
            const Icon = t.icon;
            const activeChip = t.key === type;
            const count = perTypeCounts[t.key];
            return (
              <button
                key={t.key}
                data-testid={`search-page-tab-${t.key}`}
                onClick={() => setType(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${
                  activeChip
                    ? "bg-[#FFBF00] text-black font-bold"
                    : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon size={12} />
                {t.label}
                {count !== undefined && t.key !== "all" && (
                  <span className={`ml-1 text-[9px] ${activeChip ? "text-black/60" : "text-white/40"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Results */}
        {loading && (
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 rounded-xl bg-white/[0.02] border border-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && !q.trim() && type === "all" && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-white/40">
              <TrendingUp size={12} /> Trending searches
            </div>
            <div className="flex flex-wrap gap-2">
              {trending.map((r) => (
                <button
                  key={r.q}
                  data-testid={`search-page-trending-${r.q}`}
                  onClick={() => setParams({ q: r.q })}
                  className="px-3 py-2 rounded-full bg-[#FFBF00]/5 hover:bg-[#FFBF00]/10 border border-[#FFBF00]/20 text-sm text-[#FFBF00]/90"
                >
                  {r.q}
                  <span className="ml-2 text-[10px] text-[#FFBF00]/50">{r.count}</span>
                </button>
              ))}
              {trending.length === 0 && (
                <div className="text-white/40 text-sm">No trending searches yet.</div>
              )}
            </div>
          </div>
        )}

        {!loading && q.trim() && currentResults.length === 0 && (
          <div className="text-center py-16 text-white/50" data-testid="search-page-empty">
            <div className="text-6xl mb-3 opacity-20">∅</div>
            <div className="text-lg">No results for &quot;{q}&quot;</div>
            <div className="text-sm text-white/40 mt-2">Try a different keyword or switch categories.</div>
          </div>
        )}

        {!loading && currentResults.length > 0 && (
          <div className="grid gap-3" data-testid="search-page-results">
            {currentResults.map((r, i) => (
              <ResultCard key={`${r.type}-${r.id}-${i}`} r={r} idx={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
